import React, { useEffect, useState } from 'react';
import { GroupFaceIcon } from '../../components/nucleus/faces';
import api from '../../services/api';
import Inspector, { Band, Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { usePlace } from '../../nucleus/place';
import { memberOf, sortMembers } from '../nucleus/PeopleData';
import { sizeOf, leaveConfirm, leaveGroup, leaveAsks, deleteConfirm, sharing, sharesWords } from './groupsData';
import ContractRow, { shareContractPath, accessContractPath } from './ContractRow';
import { realmShort } from '../nucleus/groupRealms';
import { dateOf } from '../../nucleus/format';

export function GroupFace({ group, size = 50 }) {
    return <GroupFaceIcon url={group.avatar_url || group.avatar} size={size} />;
}

// EVERY ACT ON ONE GROUP from your own space — the inspector's bar and the
// cell's right-click read this one array. `members`, when the inspector has
// read them, lets Leave ask its last admin their choice straight away (else the
// server says, when they leave, whether there is one to make).
export function groupActions(g, { navigate, toast, meId, refreshGroups, reload, close, members, confirm }) {
    const admin = g.role === 'admin';
    const gone = (message) => { toast.success(message); close?.(); refreshGroups?.(); reload?.(); };
    return [
        { id: 'enter', label: 'Enter group', primary: true, run: () => navigate(`/group/${g.id}/graph`) },
        { id: 'share', label: sharing(g) ? 'Your share' : 'Share', hint: sharing(g) ? 'What you share with it' : 'Share with it from your own graph', run: () => navigate(`/group/${g.id}/self/share`) },
        {
            id: 'leave', label: 'Leave', danger: true,
            confirm: leaveAsks({ role: g.role, members, meId }) ? leaveConfirm({ name: g.name }) : undefined,
            run: async () => { if (await leaveGroup(g.id, meId, confirm, { name: g.name, role: g.role, members })) gone(`You left ${g.name}`); },
        },
        {
            id: 'delete', label: 'Delete', danger: true, disabled: !admin, hint: admin ? undefined : 'Only an admin of this group can delete it',
            confirm: deleteConfirm(g.name),
            run: async () => { await api.deleteGroup(g.id); gone(`${g.name} was deleted`); },
        },
    ];
}

// ONE GROUP, from your own space: where you stand in it, who is in it (each a
// door into the group's own People), and the acts in one bar.
export default function GroupInspector({ group: g, ctx, onClose }) {
    const { navigate } = usePlace();
    const [members, setMembers] = useState(null);
    useEffect(() => {
        let live = true;
        setMembers(null);
        api.getGroupMembers(g.id)
            .then((r) => { if (live) setMembers(sortMembers((r?.members || []).map(memberOf))); })
            .catch(() => { if (live) setMembers([]); });
        return () => { live = false; };
    }, [g.id]);

    const role = g.role === 'admin' ? 'Admin' : 'Member';
    const actions = groupActions(g, { ...ctx, members: members && members.length ? members : undefined });
    const shown = (members || []).slice(0, 8);
    const count = members ? members.length : g.count;

    return (
        <Inspector
            face={<GroupFace group={g} size={56} />}
            tag={`Group · ${role}`}
            title={g.name}
            sub={g.description || undefined}
            actions={actions}
            onClose={onClose}
        >
            <Band title="You">
                <Facts items={[
                    { label: 'Your role', value: role },
                    { label: 'You can do', value: g.access?.realm || '—' },
                    { label: 'You share', value: sharesWords(g.share), tone: sharing(g) ? undefined : 'warn' },
                    g.createdBy && g.createdBy === String(ctx.meId) ? { label: 'Made', value: dateOf(g.created) || '—' } : { label: 'Joined', value: dateOf(g.joined) || '—' },
                ]} />
                {!sharing(g) && <Note>Its members reach nothing of yours. Your contract with {g.name} exists; it is empty.</Note>}
            </Band>
            <Band title="Your two contracts">
                <ContractRow
                    name={`What you share with ${g.name}`}
                    sub={`${sharing(g) ? g.share.realms.join(', ') : 'Nothing yet'} · from your own graph`}
                    // a pill only when you share something, as the member panel: an
                    // empty contract is not Active
                    status={sharing(g) ? (g.share.active === false ? 'paused' : 'active') : null}
                    onOpen={g.share?.epId ? () => navigate(shareContractPath(g.share.epId)) : null}
                />
                <ContractRow
                    name={`What you can do in ${g.name}`}
                    sub={[g.access?.realm, realmShort({ kind: g.access?.kind })].filter(Boolean).join(' · ') || '—'}
                    status={g.access?.state || null}
                    onOpen={g.access?.epId ? () => navigate(accessContractPath(g.id, g.access.epId)) : null}
                />
                <Note>Every member has both, admins too. Removing your share empties the first; it is never deleted.</Note>
            </Band>
            <Band
                title={`Who is in it${count != null ? ` · ${count}` : ''}`}
                aside={<button type="button" className="nx-link" onClick={() => navigate(`/group/${g.id}/people`)}>All members</button>}
            >
                {members && members.length === 0 && <Note>Could not read who is in it.</Note>}
                {shown.map((m) => (
                    <Row
                        key={m.id}
                        name={m.name}
                        // role, then realm: "Admin · Full Access", "Member · Members"
                        sub={[m.role === 'admin' ? 'Admin' : 'Member', m.realm].filter(Boolean).join(' · ')}
                        end={m.joined ? `joined ${dateOf(m.joined)}` : undefined}
                        onPress={() => navigate(`/group/${g.id}/people?open=${m.id}`)}
                    />
                ))}
                {members && members.length > shown.length && <Note>And {members.length - shown.length} more.</Note>}
            </Band>
            <Band title="About">
                <Facts items={[
                    { label: 'Encryption', value: g.encrypted ? 'On' : 'Off' },
                    { label: 'Size', value: sizeOf(count) || '—' },
                ]} />
                <Note>Its settings, encryption and public page are in Settings, inside the group.</Note>
            </Band>
        </Inspector>
    );
}
